import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  Vibration,
  View,
} from 'react-native';

import { useSocket } from '../hooks/useSocket';
import httpClient from '../utils/httpClient';

const { width: _width, height: _height } = Dimensions.get('window');

interface IncomingCallScreenProps {
  callId: string;
  callerId: string;
  isVideo: boolean;
}

type IncomingCallScreenRouteProp = RouteProp<{ params: IncomingCallScreenProps }, 'params'>;

interface CallerInfo {
  id: string;
  nickname: string;
  avatar?: string;
  status?: 'online' | 'offline' | 'busy';
}

export default function IncomingCallScreen() {
  const route = useRoute<IncomingCallScreenRouteProp>();
  const navigation = useNavigation();
  const { callId, callerId, isVideo } = route.params;
  const { on, off, emit } = useSocket();

  const [pulseAnim] = useState(() => new Animated.Value(1));
  const [callerInfo, setCallerInfo] = useState<CallerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [callStatus, setCallStatus] = useState<'ringing' | 'accepted' | 'rejected' | 'missed'>(
    'ringing',
  );
  const vibrationRef = useRef<boolean>(true);

  // Fetch caller information from server
  const fetchCallerInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(`/users/${callerId}`);
      const userData = response.data?.user || response.data;

      setCallerInfo({
        id: userData.id,
        nickname: userData.nickname || userData.name || '未知用户',
        avatar: userData.avatar,
        status: userData.status,
      });
    } catch (error) {
      console.error('Failed to fetch caller info:', error);
      // Fallback to default
      setCallerInfo({
        id: callerId,
        nickname: '未知用户',
        avatar: undefined,
      });
    } finally {
      setLoading(false);
    }
  }, [callerId]);

  // Setup vibration and animation
  useEffect(() => {
    // Fetch caller info
    fetchCallerInfo();

    // Vibration pattern: vibrate 500ms, pause 1000ms, repeat
    const vibrationInterval = setInterval(() => {
      if (vibrationRef.current) {
        Vibration.vibrate([0, 500, 1000, 500, 1000]);
      }
    }, 3000);

    // Initial vibration
    Vibration.vibrate([0, 500, 1000, 500, 1000]);

    // Pulsing animation for avatar
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ]),
    ).start();

    return () => {
      clearInterval(vibrationInterval);
      Vibration.cancel();
      pulseAnim.stopAnimation();
    };
  }, [fetchCallerInfo, pulseAnim]);

  // Listen for call cancellation from caller
  useEffect(() => {
    const handleCallCancelled = (data: { callId: string }) => {
      if (data.callId === callId) {
        setCallStatus('missed');
        vibrationRef.current = false;
        Vibration.cancel();

        // Show missed call message briefly then go back
        setTimeout(() => {
          navigation.goBack();
        }, 2000);
      }
    };

    const handleCallEnded = (data: { callId: string }) => {
      if (data.callId === callId) {
        vibrationRef.current = false;
        Vibration.cancel();
        navigation.goBack();
      }
    };

    on('call:cancelled', handleCallCancelled);
    on('call:ended', handleCallEnded);

    return () => {
      off('call:cancelled', handleCallCancelled);
      off('call:ended', handleCallEnded);
    };
  }, [callId, on, off, navigation]);

  // Accept the incoming call
  const handleAccept = useCallback(() => {
    if (callStatus !== 'ringing') return;

    vibrationRef.current = false;
    Vibration.cancel();
    setCallStatus('accepted');

    // Send accept signal to server
    emit('call:accept', {
      callId,
      callerId,
    });

    // Navigate to call screen
    navigation.navigate('Call' as any, {
      callId,
      isVideo,
    });
  }, [callId, callerId, isVideo, callStatus, emit, navigation]);

  // Reject the incoming call
  const handleReject = useCallback(() => {
    if (callStatus !== 'ringing') return;

    vibrationRef.current = false;
    Vibration.cancel();
    setCallStatus('rejected');

    // Send reject signal to server
    emit('call:reject', {
      callId,
      callerId,
    });

    // Go back to previous screen
    navigation.goBack();
  }, [callId, callerId, callStatus, emit, navigation]);

  // Get caller display name
  const getCallerName = () => {
    if (loading) return '加载中...';
    return callerInfo?.nickname || '未知用户';
  };

  // Get caller avatar (emoji fallback)
  const getCallerAvatar = () => {
    if (callerInfo?.avatar) {
      return null; // Will show image
    }
    return isVideo ? '📹' : '📞';
  };

  return (
    <View style={styles.container}>
      <Animated.View style={[styles.avatarContainer, { transform: [{ scale: pulseAnim }] }]}>
        {callerInfo?.avatar ? (
          <Image source={{ uri: callerInfo.avatar }} style={styles.avatarImage} />
        ) : (
          <Text style={styles.avatar}>{getCallerAvatar()}</Text>
        )}
      </Animated.View>

      <View style={styles.info}>
        <Text style={styles.callType}>{isVideo ? '视频通话' : '语音通话'}</Text>
        <Text style={styles.callerName}>{getCallerName()}</Text>
        <Text style={styles.callingText}>
          {callStatus === 'ringing' ? '正在呼入...' : '已结束'}
        </Text>
      </View>

      <View style={styles.actions}>
        <TouchableOpacity style={[styles.actionButton, styles.rejectButton]} onPress={handleReject}>
          <Text style={styles.rejectIcon}>❌</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.actionButton, styles.acceptButton]} onPress={handleAccept}>
          <Text style={styles.acceptIcon}>✓</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={styles.swipeHint} onPress={handleReject}>
        <Text style={styles.swipeText}>向下滑动拒绝</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  acceptButton: {
    backgroundColor: 'rgba(82,196,26,0.8)',
  },
  acceptIcon: {
    fontSize: 36,
  },
  actionButton: {
    alignItems: 'center',
    borderRadius: 40,
    height: 80,
    justifyContent: 'center',
    width: 80,
  },
  actions: {
    bottom: 100,
    flexDirection: 'row',
    gap: 40,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
  },
  avatar: {
    fontSize: 80,
  },
  avatarContainer: {
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 75,
    height: 150,
    justifyContent: 'center',
    marginTop: -100,
    width: 150,
  },
  avatarImage: {
    borderRadius: 75,
    height: 150,
    width: 150,
  },
  callType: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 16,
    marginBottom: 12,
  },
  callerName: {
    color: '#fff',
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  callingText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 18,
  },
  container: {
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    flex: 1,
  },
  info: {
    alignItems: 'center',
    marginTop: 40,
  },
  rejectButton: {
    backgroundColor: 'rgba(255,77,79,0.8)',
  },
  rejectIcon: {
    fontSize: 36,
  },
  swipeHint: {
    alignItems: 'center',
    bottom: 40,
    left: 0,
    paddingVertical: 8,
    position: 'absolute',
    right: 0,
  },
  swipeText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
  },
});
